import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  signal,
  ViewChild,
} from '@angular/core';

import { SkIconComponent } from '@mercadoni/stark-ui';

import { ClusterService } from '../../../core/services/context/cluster.service';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';

@Component({
  selector: 'nf-cluster-selector',
  imports: [SkIconComponent, ClickOutsideDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
  },
  styleUrl: './nf-cluster-selector.component.css',
  template: `
    <div nfClickOutside (clickOutside)="close()">
      <button #trigger class="nf-cs-trigger" type="button" (click)="toggle()">
        <span class="nf-cs-dot" [style.background]="clusterService.activeCluster().color"></span>
        {{ clusterService.activeCluster().label }}
        <sk-icon name="chevron-down" size="sm" />
      </button>

      @if (open()) {
        <div
          class="nf-cs-panel"
          [style.top.px]="panelTop()"
          [style.left.px]="panelLeft()"
          [style.right]="panelRight()">
          @for (cluster of clusterService.availableClusters(); track cluster.clusterName) {
            <button
              type="button"
              class="nf-cs-item"
              [class.active]="cluster.clusterName === clusterService.activeCluster().clusterName"
              (click)="select(cluster.clusterName)">
              <span class="nf-cs-dot" [style.background]="cluster.color"></span>
              <span class="nf-cs-item-labels">
                <span>{{ cluster.label }}</span>
              </span>
              @if (cluster.clusterName === clusterService.activeCluster().clusterName) {
                <sk-icon name="check" size="sm" />
              }
            </button>
          }
        </div>
      }
    </div>
  `,
})
export class NfClusterSelectorComponent {
  readonly clusterService = inject(ClusterService);

  @ViewChild('trigger') triggerRef!: ElementRef<HTMLButtonElement>;

  readonly open = signal(false);
  readonly panelTop = signal(0);
  readonly panelLeft = signal(0);
  readonly panelRight = signal('auto');

  toggle(): void {
    if (this.open()) {
      this.open.set(false);
    } else {
      this.updatePanelPosition();
      this.open.set(true);
    }
  }

  select(clusterName: string): void {
    this.clusterService.select(clusterName);
    this.open.set(false);
  }

  close(): void {
    this.open.set(false);
  }

  private updatePanelPosition(): void {
    const btn = this.triggerRef?.nativeElement;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const panelWidth = 220;
    const viewportWidth = window.innerWidth;

    // Prefer aligning to the right edge of the button; flip left if it would overflow
    const rightAligned = viewportWidth - rect.right;
    if (rightAligned + panelWidth < viewportWidth) {
      this.panelTop.set(rect.bottom + 6);
      this.panelLeft.set(rect.right - panelWidth);
      this.panelRight.set('auto');
    } else {
      this.panelTop.set(rect.bottom + 6);
      this.panelLeft.set(rect.left);
      this.panelRight.set('auto');
    }
  }
}
